<template>
  <div class="cruise-detail" :class="{ 'cruise-detail--unpublished': scheduleUnpublished }">
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading cruise information...</p>
    </div>
    
    <!-- CP-202: unknown or hidden sailing (API 404): not-found page, not a retryable error. -->
    <not-found v-else-if="notFound" variant="cruise" />

    <div v-else-if="error" class="error-container app-error-state">
      <i class="fas fa-exclamation-circle app-error-icon"></i>
      <p>{{ error }}</p>
      <button type="button" class="app-retry-btn" @click="retryLoad">Try Again</button>
    </div>
    
    <div v-else class="content-container">
      <!-- Cruise Header Card -->
      <div class="card cruise-header-card">
        <div class="cruise-header">
          <div class="cruise-title">
            <div class="title-with-badges">
              <h1 class="cs-title-case hero-title">{{ titleCase(cruiseInfo.name) }}</h1>
              <div class="deal-badges">
                <deal-explainer-popover
                  v-if="showHotDeal"
                  title="🔥 Hot Deal: Price Drop"
                  :definitions="hotDealDefinitions"
                  aria-label="Hot deal details"
                  align-right
                >
                  <template #trigger>
                    <span class="deal-badge hot-deal-badge">
                      🔥 HOT DEAL <span class="deal-badge-info" aria-hidden="true">ℹ</span>
                    </span>
                  </template>
                </deal-explainer-popover>
                <deal-explainer-popover
                  v-if="showBestDeal"
                  title="⭐ Best Deal: Exceptional Value"
                  :definitions="bestDealDefinitions"
                  aria-label="Best deal details"
                  align-right
                >
                  <template #trigger>
                    <span class="deal-badge best-deal-badge">
                      ⭐ BEST DEAL <span class="deal-badge-info" aria-hidden="true">ℹ</span>
                    </span>
                  </template>
                </deal-explainer-popover>
              </div>
            </div>
            <div class="cruise-subtitle">
              <span v-if="!shipUnknown" class="ship-name">{{ cruiseInfo.ship_name }}</span>
              <span v-if="!shipUnknown && cruiseInfo.ship_name && cruiseInfo.cruise_line" class="subtitle-sep" aria-hidden="true">·</span>
              <span class="cruise-line">{{ cruiseInfo.cruise_line }}</span>
            </div>
            <!-- CP-217: same server-derived label as the Browse card. -->
            <p v-if="cruiseInfo.package_label" class="cruise-package">{{ cruiseInfo.package_label }}</p>
            <p v-if="scheduleUnpublished" class="schedule-unpublished" role="status">Schedule not yet published</p>
          </div>
          <div
            v-if="nightsLabel(cruiseInfo.duration) || realDeparturePort || realDestination"
            class="cruise-summary"
            aria-label="Trip highlights"
          >
            <div v-if="nightsLabel(cruiseInfo.duration)" class="summary-item">
              <i class="fas fa-moon" aria-hidden="true"></i>
              <!-- CP-278: package sailings say 'N-night cruise[, part of an M-night package]' (packageNights.js). -->
              <span>{{ sailingNightsLabel(cruiseInfo) }}</span>
            </div>
            <div v-if="realDeparturePort" class="summary-item">
              <i class="fas fa-ship" aria-hidden="true"></i>
              <span>From {{ realDeparturePort }}</span>
            </div>
            <div v-if="realDestination" class="summary-item">
              <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
              <span>{{ realDestination }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Image Gallery -->
      <div v-if="cruiseImages.length > 0" class="card">
        <h2>Photos</h2>
        <image-gallery :images="cruiseImages" />
      </div>
      
      <!-- CP-226: the DOM order IS the reading/tab order. Phone/tablet (< 1024px) is one stack:
           price card, Itinerary, Price Alert, Itinerary Summary, Sail Dates, then ship / deck /
           map / price history / reviews. Desktop (>= 1024px) is two real columns, main then
           sidebar. No CSS `order` reshuffles anything; the layout picks the markup. -->
      <detail-layout :stacked="stacked">
        <template #booking>
            <!-- Booking Card: price + primary CTA dominate; Share/Print buried (CP-32 C).
                 CP-120: while Book is off, the primary CTA is Price Alert and the
                 not-bookable status is quiet text under it.
                 An unpublished shell has no shoppable From price and no book path. -->
            <div class="card booking-card">
              <div v-if="scheduleUnpublished" class="unpublished-booking" role="status">
                <p class="schedule-unpublished">Schedule not yet published</p>
                <p class="unpublished-booking-note">Not available to book.</p>
              </div>
              <book-now-button
                v-else
                :cruise-id="cruiseId"
                :price="currentPrice"
                :updated-label="priceUpdatedLabel"
                :age-note="staleAgeNote"
                :hidden-reason="hiddenPriceLabel"
                :provider="cruiseInfo.cruise_line"
                :cabin-type="cruiseInfo.current_cabin_type"
                :affiliate-enabled="affiliateEnabled"
                :partner-url="bookPartnerUrl"
                :landing-tier="cruiseInfo.affiliate_landing_tier || ''"
                :line-name="cruiseInfo.cruise_line || ''"
                :collected-at="cruiseInfo.price_updated_at || ''"
                alert-target="price-alert"
              />
              <div class="booking-share-rail">
                <share-buttons
                  compact
                  :title="titleCase(cruiseInfo.name)"
                  :description="shareDescription"
                />
              </div>
            </div>
        </template>
        <template #itinerary>
            <!-- Itinerary (CP-160 / CP-214): ONE port-by-port timeline. Day list, title
                 port-name summary, or the honest empty state. Never omitted. -->
            <div class="card itinerary-card">
              <h2>Itinerary</h2>
              <detail-timeline
                :model="timelineModel"
                :title-ports="itineraryTitlePorts"
                :template-note="itinerarySection.templateNote"
                :title-fallback-note="itinerarySection.titleFallbackNote"
                :empty-message="itineraryEmptyMessage"
                :show-disclosure="itineraryHasExcursionSuggestions"
                :excursions-loading="excursionsLoading"
              />
            </div>
        </template>
        <template #alert>
            <!-- Price Alert: nothing to watch until a fare exists. -->
            <price-alert
              v-if="!scheduleUnpublished"
              id="price-alert"
              :cruise-id="Number(cruiseId)"
              :cabin-types="uniqueCabinTypes"
            />
        </template>
        <template #summary>
            <!-- Itinerary Summary Card -->
            <div v-if="!scheduleUnpublished" class="card itinerary-summary-card">
              <h3>Itinerary Summary</h3>
              <div class="itinerary-summary-content">
                <div class="summary-item">
                  <i class="fas fa-calendar-alt"></i>
                  <div>
                    <span class="summary-label">Departure</span>
                    <span class="summary-value">{{ formatDepartureDisplay(cruiseInfo.departure_date) }}</span>
                  </div>
                </div>
                <div class="summary-item">
                  <i class="fas fa-ship"></i>
                  <div>
                    <span class="summary-label">From</span>
                    <span class="summary-value">{{ titleCase(cruiseInfo.departure_port) }}</span>
                  </div>
                </div>
                <div class="summary-item">
                  <i class="fas fa-map-marker-alt"></i>
                  <div>
                    <span class="summary-label">Destination</span>
                    <span class="summary-value">{{ titleCase(cruiseInfo.destination) }}</span>
                  </div>
                </div>
                <div class="summary-item">
                  <i class="fas fa-moon"></i>
                  <div>
                    <span class="summary-label">Duration</span>
                    <span class="summary-value">{{ nightsLabel(cruiseInfo.duration) }}</span>
                  </div>
                </div>
                <div v-if="itineraryCounts.ports > 0" class="summary-item">
                  <i class="fas fa-anchor"></i>
                  <div>
                    <span class="summary-label">Ports</span>
                    <span class="summary-value">{{ portsLabel(itineraryCounts.ports) }}</span>
                  </div>
                </div>
                <div v-if="getFirstPort" class="summary-item">
                  <i class="fas fa-route"></i>
                  <div>
                    <span class="summary-label">First Port</span>
                    <span class="summary-value">{{ getFirstPort }}</span>
                  </div>
                </div>
                <div v-if="getLastPort" class="summary-item">
                  <i class="fas fa-flag-checkered"></i>
                  <div>
                    <span class="summary-label">Last Port</span>
                    <span class="summary-value">{{ getLastPort }}</span>
                  </div>
                </div>
                <div v-if="itineraryCounts.seaDays > 0" class="summary-item">
                  <i class="fas fa-water"></i>
                  <div>
                    <span class="summary-label">Days at Sea</span>
                    <span class="summary-value">{{ itineraryCounts.seaDays }}</span>
                  </div>
                </div>
              </div>
            </div>
        </template>
        <template #dates>
            <!-- Available Sail Dates Card: no picker when the schedule is unpublished. -->
            <div v-if="!scheduleUnpublished" class="card sail-dates-card">
              <h3>Available Sail Dates</h3>
              <!-- CP-238: each row is a real link (Tab, Enter, Space) with a name that says which date
                   and whether it is the current sailing; aria-current marks the one on screen. -->
              <ul v-if="visibleSailDates.length > 0" class="sail-dates-list" role="list" aria-label="Available sail dates">
                <li v-for="(date, index) in visibleSailDates" :key="index" class="sail-date-entry">
                <a
                  class="sail-date-item"
                  :class="{ 'current-date': isSameSailDate(date) }"
                  :href="sailDateHref(cruiseId, date)"
                  :aria-label="sailDateRowLabel(formatSailDate(date), isSameSailDate(date))"
                  :aria-current="isSameSailDate(date) ? 'date' : null"
                  @click="onSailDateClick($event, date)"
                  @keydown.space.prevent="navigateToSailDate(date)"
                >
                  <div class="date-info">
                    <i class="fas fa-calendar-alt" aria-hidden="true"></i>
                    <span>{{ formatSailDate(date) }}</span>
                  </div>
                  <div v-if="isSameSailDate(date)" class="current-indicator">
                    Current
                  </div>
                  <div v-else class="view-indicator" aria-hidden="true">
                    <i class="fas fa-chevron-right"></i>
                  </div>
                </a>
                </li>
              </ul>
              <div v-else class="no-dates-message">
                <p>No upcoming sail dates for this itinerary.</p>
              </div>
            </div>
        </template>
        <template #ship>
            <!-- Ship Information: a name alone is not a ship profile. CP-96: the
                 generic "vessel offering amenities" blurb, cabin categories in
                 the amenities list, and blank specs are not facts either. -->
            <ship-details
              v-if="showShipModule"
              :ship-info="shipInfo"
              :ship-name="cruiseInfo.ship_name"
            />
        </template>
        <template #deck>
            <!-- Deck Plan -->
            <div v-if="shipInfo && shipInfo.deck_plan_url" class="card">
              <h2>Deck Plans</h2>
              <deck-plan :deck-plan-url="shipInfo.deck_plan_url" />
            </div>
        </template>
        <template #map>
            <!-- Interactive Map -->
            <div v-if="portsOfCall.length > 0 || itinerarySchedule.length > 0" class="card">
              <h2>Cruise Route</h2>
              <interactive-map :ports="portsOfCall" :itinerary-schedule="itinerarySchedule" />
            </div>
        </template>
        <template #history>
            <!-- Price History stays on the page. Chart only with a shoppable fare and
                 ≥2 observation days so Current/Lowest/Highest cannot sit beside
                 "Price unavailable" (CP-69/CP-90). A stale fare says
                 "Price not current" on the book box instead (CP-100) and still
                 keeps this empty state. One observation day says so (CP-94).
                 Otherwise the history is unavailable (CP-91). Do not drop the
                 section. -->
            <div class="card price-chart-card">
              <h2>Price History</h2>
              <div v-if="!hasPriceHistoryChart" class="no-data-message" role="status">
                <i class="fas fa-chart-line" aria-hidden="true"></i>
                <p v-if="isSinglePriceObservation">Only one price observation so far</p>
                <p v-else>Price history unavailable</p>
              </div>
              <template v-else>
              <!-- Toggle button for showing all cabin types. Hidden when there is
                   only one cabin type, since expanding would reveal nothing (CP-135). -->
              <div v-if="showCabinTypeToggle" class="cabin-toggle-container">
                <button 
                  class="cabin-toggle-button" 
                  @click="showAllCabinTypes = !showAllCabinTypes"
                >
                  <span v-if="showAllCabinTypes">
                    <i class="fas fa-chevron-up"></i> Show only Interior
                  </span>
                  <span v-else>
                    <i class="fas fa-chevron-down"></i> Show all cabin types
                  </span>
                </button>
              </div>
            
              <!-- Display pricing for all cabin types when showing all, or just Interior when collapsed -->
              <div v-if="cabinTypesExpanded && Object.keys(pricesByType).length > 0" class="all-cabin-prices">
                <div v-for="[cabinType, data] in orderedCabinTiles" :key="cabinType" class="cabin-price-card">
                  <h3 class="cabin-type">{{ cabinType }}</h3>
                  <div class="cabin-prices">
                    <!-- CP-268: Current only from the headline's collection. Without one the label
                         stays and the value slot carries a muted note (no stale dollar). -->
                    <div class="price-stat" :class="{ 'price-stat--no-current': data.current == null }">
                      <span class="stat-label">Current</span>
                      <span v-if="data.current != null" class="stat-value">${{ formatPriceAmount(data.current) }}</span>
                      <span v-else class="stat-note">Not in latest update</span>
                    </div>
                    <div class="price-stat">
                      <span class="stat-label">Lowest</span>
                      <span class="stat-value">${{ formatPriceAmount(data.lowest) }}</span>
                    </div>
                    <div class="price-stat">
                      <span class="stat-label">Highest</span>
                      <span class="stat-value">${{ formatPriceAmount(data.highest) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            
              <!-- No cabin pricing in the selected time range -->
              <div v-else-if="Object.keys(pricesByType).length === 0" class="price-summary">
                <p style="color: var(--color-text-muted); text-align: center; width: 100%;">No cabin pricing in the selected time range.</p>
              </div>
            
              <!-- Show single cabin pricing when collapsed -->
              <div v-else class="price-summary">
                <div v-for="(data, cabinType) in visiblePricesByType" :key="cabinType" class="cabin-price-card">
                  <h3 class="cabin-type">{{ cabinType }}</h3>
                  <div class="cabin-prices">
                    <!-- CP-268: Current only from the headline's collection. Without one the label
                         stays and the value slot carries a muted note (no stale dollar). -->
                    <div class="price-stat" :class="{ 'price-stat--no-current': data.current == null }">
                      <span class="stat-label">Current</span>
                      <span v-if="data.current != null" class="stat-value">${{ formatPriceAmount(data.current) }}</span>
                      <span v-else class="stat-note">Not in latest update</span>
                    </div>
                    <div class="price-stat">
                      <span class="stat-label">Lowest</span>
                      <span class="stat-value">${{ formatPriceAmount(data.lowest) }}</span>
                    </div>
                    <div class="price-stat">
                      <span class="stat-label">Highest</span>
                      <span class="stat-value">${{ formatPriceAmount(data.highest) }}</span>
                    </div>
                  </div>
                </div>
              
                <div class="price-trend" v-if="priceTrend !== null">
                  <span :class="['trend-indicator', priceTrend > 0 ? 'trend-up' : 'trend-down']">
                    <i :class="priceTrend > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
                    {{ Math.abs(priceTrend) }}%
                  </span>
                  <span class="trend-label">{{ trendLabel }}</span>
                </div>
              </div>
            
              <div class="chart-controls">
                <div class="date-range-selector">
                  <button 
                    v-for="range in ['7', '30', '90', 'all']" 
                    :key="range"
                    @click="dateRange = range" 
                    :class="['range-button', dateRange === range ? 'active' : '']"
                  >
                    {{ range === 'all' ? 'All Time' : range + ' Days' }}
                  </button>
                </div>
              </div>
            
              <div class="chart-wrapper">
                <div class="chart-container-wrapper">
                  <div :class="['chart-container', { 'chart-container--legend': chartLegendShown }]">
                    <line-chart 
                      v-if="chartData.labels.length > 0" 
                      :chart-data="chartData" 
                      :options="chartOptions"
                    />
                    <div v-else-if="hasPriceHistoryChart && priceData.length > 0" class="no-data-message">
                      <i class="fas fa-chart-line"></i>
                      <p>No price data in the selected time range. Try All Time to see the full history.</p>
                      <button type="button" class="range-button" @click="dateRange = 'all'">All Time</button>
                    </div>
                    <div v-else class="no-data-message">
                      <i class="fas fa-chart-line"></i>
                      <p>No price history available for this cruise.</p>
                    </div>
                  </div>
                </div>
              </div>
              <!-- CP-290 (AC7): HAL offer-code-like fares are history only. The API keeps them out
                   of the chart and tiles (no muted line); this plain muted note names the plotted
                   cabin without a price or count. -->
              <p v-if="offerOnlyHistoryNote" class="offer-only-note" data-test="offer-only-note">
                {{ offerOnlyHistoryNote }}
              </p>
            
              <!-- Pricing Enhancements -->
              <pricing-enhancements 
                v-if="typeof currentPrice === 'number'"
                :current-price="currentPrice"
                :lowest-price="lowestPrice"
                :highest-price="highestPrice"
                :duration="cruiseInfo.duration"
                :price-history="priceData"
                :deal-status="cruiseInfo.deal_status"
                :sail-date="effectiveCurrentSailDate"
                :potential-savings="cruiseInfo.potential_savings"
                :observed-series="observedCabinSeries"
              />
              </template>
            </div>
        </template>
        <template #reviews>
            <!-- Reviews: an empty shell has no voyage to review. CP-96: hidden
                 until at least one approved review exists (no 0-star summary,
                 no filter/sort bar). v-show keeps the list mounted to fetch. -->
            <div v-if="!scheduleUnpublished" v-show="reviewCount > 0" class="card">
              <h2>Reviews</h2>
              <review-list :cruise-id="Number(cruiseId)" @count="reviewCount = $event" />
            </div>
        </template>
      </detail-layout>
    </div>
    <!-- CP-202: no recommendations under a not-found page.
         CP-211: and none beside the loading spinner or the error state. Rendered there it sat
         on screen, then the page content pushed it away (CLS ~0.6) and moved the footer. -->
    <div v-if="!loading && !notFound && !error" class="similar-cruises-section">
      <div class="card">
        <h2>You Might Also Like</h2>
        <similar-cruises 
          :cruise-id="Number(cruiseId)" 
          :current-cruise="cruiseInfo"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import DetailLayout, { STACKED_QUERY } from '@/components/DetailLayout.vue';
import { useRoute, useRouter } from 'vue-router';
import LineChart from '@/components/LineChart.vue';
import { mergeChartOptions, detailResponsiveOptions, detailLegendShown, NARROW_CHART_QUERY, priceYAxis } from '@/utils/chartOptions';
import BookNowButton from '@/components/BookNowButton.vue';
import { affiliateState, sellsLine } from '@/utils/affiliateFlag';
import SimilarCruises from '@/components/SimilarCruises.vue';
import ShipDetails from '@/components/ShipDetails.vue';
import ImageGallery from '@/components/ImageGallery.vue';
import PricingEnhancements from '@/components/PricingEnhancements.vue';
import InteractiveMap from '@/components/InteractiveMap.vue';
import DetailTimeline from '@/components/DetailTimeline.vue';
import ReviewList from '@/components/ReviewList.vue';
import PriceAlert from '@/components/PriceAlert.vue';
import DeckPlan from '@/components/DeckPlan.vue';
import ShareButtons from '@/components/ShareButtons.vue';
import DealExplainerPopover from '@/components/DealExplainerPopover.vue';
import NotFound from '@/views/NotFound.vue';
import { applyNoindex, isNotFoundError } from '@/utils/notFound';
import api from '@/services/api';
import { formatSailDate as formatSailDateUtil, toDateString as toDateStringUtil } from '@/utils/formatSailDate';
import { pickDefaultSailDate } from '@/utils/defaultSailDate';
import { cabinTilesCheapToPremium, chartSeriesPoints } from '@/utils/cabinPeak';
import { cabinCurrentFare } from '@/utils/cabinCurrentFare';
import { offerOnlyNote } from '@/utils/offerOnlyHistory';
import { collapsedCabinTypes, showsCabinTypeToggle } from '@/utils/cabinTypeToggle';
import { isSinglePriceObservation as singlePriceObservation, observationDayCount, priceHistoryLineStyle } from '@/utils/priceHistoryChart';
import { cabinTrajectoryPercent } from '@/utils/bookingTrend';
import { hiddenPriceReason, listingPriceCue, showsBestDealBadge, showsHotDealBadge, showsListingPrice, staleAgeStamp } from '@/utils/priceFreshness';
import {
  parsePortsOfCall,
  portsFromScheduleDays,
  buildItinerarySection,
} from '@/utils/itinerary';
import { isShipUnknown, isUnpublishedShell, showsShoppableFare } from '@/utils/unpublishedShell';
import { buildTimelineModel, portsLabel, summarizeItinerary } from '@/utils/detailTimeline';
import { hasShipProfile } from '@/utils/detailModules';
import { isPlainActivation, sailDateHref, sailDateRowLabel } from '@/utils/detailKeyboard';

import { formatPriceAmount, formatPrice } from '@/utils/formatMoney';
import { formatPlaceTitle } from '@/utils/formatPlaceTitle';
import { nightsLabel as plainNightsLabel, sailingNightsLabel } from '@/utils/packageNights';
export default {
  name: 'CruiseDetail',
  components: {
    LineChart,
    BookNowButton,
    SimilarCruises,
    ShipDetails,
    ImageGallery,
    PricingEnhancements,
    InteractiveMap,
    DetailTimeline,
    DetailLayout,
    ReviewList,
    PriceAlert,
    DeckPlan,
    ShareButtons,
    DealExplainerPopover,
    NotFound
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const cruiseInfo = ref({});
    const priceData = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const notFound = ref(false);
    const cruiseId = computed(() => route.params.id);
    // CP-142: Detail Book CTA follows the single runtime flag from /api/config.
    // The partner URL is only used when the flag is ON (BookNowButton gates it).
    const affiliateEnabled = computed(() => affiliateState.enabled);
    // CP-141: no partner URL (so no Book link) when CruiseDirect doesn't sell the line.
    const bookPartnerUrl = computed(() => (
      cruiseId.value && sellsLine(cruiseInfo.value && cruiseInfo.value.cruise_line)
        ? `/api/affiliate/redirect?cruise_id=${encodeURIComponent(cruiseId.value)}&partner=cruisedirect&source=detail`
        : null
    ));
    const currentSailDate = ref(null);
    // Sail dates are calendar days. "Today" is UTC midnight, matching the
    // prices API and Browse's past-cruise cutoff.
    const sailDay = (value) => toDateStringUtil(value);
    const todayUtcDay = () => {
      const now = new Date();
      const month = String(now.getUTCMonth() + 1).padStart(2, '0');
      const day = String(now.getUTCDate()).padStart(2, '0');
      return `${now.getUTCFullYear()}-${month}-${day}`;
    };
    const isUpcomingSail = (value) => {
      const day = sailDay(value);
      return Boolean(day) && day >= todayUtcDay();
    };
    const showAllCabinTypes = ref(false);
    const cruiseImages = ref([]);
    // One excursions fetch feeds the whole Detail timeline (CP-160).
    const excursionsPayload = ref(null);
    const excursionsLoading = ref(true);
    const shipInfo = ref(null);


    // Always mounted. Day list when schedule exists; else title port-name
    // summary (CP-130); else exact "Itinerary not published". Calendar dates:
    // matching stamped rows, or CP-223 derived from departure when undated.
    const itinerarySection = computed(() => buildItinerarySection(
      cruiseInfo.value.itinerary_schedule,
      effectiveCurrentSailDate.value,
      cruiseInfo.value.name,
      cruiseInfo.value.ports_of_call,
      cruiseInfo.value.cruise_line,
    ));

    const itinerarySchedule = computed(() => itinerarySection.value.days);
    // Schedule and ports may already be lists (prod) or a JSON string (older rows).
    // CP-165: empty ports_of_call falls back to the ports on the day-by-day schedule.
    const portsOfCall = computed(() => {
      const stored = parsePortsOfCall(cruiseInfo.value.ports_of_call);
      return stored.length ? stored : portsFromScheduleDays(itinerarySchedule.value);
    });
    const itineraryTitlePorts = computed(() => itinerarySection.value.titlePorts || []);

    const scheduleUnpublished = computed(() => isUnpublishedShell(cruiseInfo.value));
    // CP-200: a shell listing is viewable but must never be indexed. nginx
    // already sends X-Robots-Tag: noindex for it; this meta covers client-side
    // navigation and removes itself when the shopper moves to another sailing.
    let removeShellNoindex = null;
    watch(scheduleUnpublished, (isShell) => {
      if (removeShellNoindex) { removeShellNoindex(); removeShellNoindex = null; }
      if (isShell && typeof document !== 'undefined') removeShellNoindex = applyNoindex(document);
    }, { immediate: true });
    // CP-226: phone/tablet render ONE stack in reading order, desktop two columns. Read synchronously
    // so the first render already has the right markup (no flash, no layout shift); no matchMedia
    // (old browser, tests) = the desktop two-column markup.
    const stackedMql = (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
      ? window.matchMedia(STACKED_QUERY) : null;
    const stacked = ref(!!(stackedMql && stackedMql.matches));
    const onStackedChange = (e) => { stacked.value = e.matches; };
    if (stackedMql) {
      if (stackedMql.addEventListener) stackedMql.addEventListener('change', onStackedChange);
      else if (stackedMql.addListener) stackedMql.addListener(onStackedChange);
    }
    onBeforeUnmount(() => {
      if (removeShellNoindex) { removeShellNoindex(); removeShellNoindex = null; }
      if (stackedMql) {
        if (stackedMql.removeEventListener) stackedMql.removeEventListener('change', onStackedChange);
        else if (stackedMql.removeListener) stackedMql.removeListener(onStackedChange);
      }
    });
    const shipUnknown = computed(() => isShipUnknown(cruiseInfo.value));
    // CP-96: every Detail (published or shell) needs at least one real ship
    // fact: a spec, a ship-specific description, a real amenity, or a deck plan.
    const showShipModule = computed(() => {
      if (shipUnknown.value || !cruiseInfo.value.ship_name) return false;
      return hasShipProfile(shipInfo.value, cruiseInfo.value.ship_name);
    });
    // Approved reviews for this cruise; the Reviews card hides at 0.
    const reviewCount = ref(0);
    const realDeparturePort = computed(() => {
      const port = String(cruiseInfo.value.departure_port || '').trim();
      if (!port || /^(unknown|n\/a|na|none|null|tbd|missing port content)$/i.test(port)) return '';
      return formatPlaceTitle(port);
    });
    const realDestination = computed(() => {
      const destination = String(cruiseInfo.value.destination || '').trim();
      if (!destination || /^(unknown|n\/a|na|none|null|tbd)$/i.test(destination)) return '';
      return formatPlaceTitle(destination);
    });
    const itineraryEmptyMessage = computed(() => {
      if (itinerarySchedule.value.length) return '';
      if (scheduleUnpublished.value) return 'Schedule not yet published';
      return itinerarySection.value.emptyMessage;
    });

    // "From" price — server lead-in, and only when that collection is ≤48h.
    // A missing fare is "Price unavailable". A recorded fare that is not fresh
    // is "Price not current" (CP-100) — the dollar stays hidden either way.
    // An unpublished shell keeps any stored fare off the shoppable widget.
    const currentPrice = computed(() => {
      const info = cruiseInfo.value;
      if (!showsShoppableFare(info, showsListingPrice(info))) return null;
      return Number(info.current_price);
    });

    const priceUpdatedLabel = computed(() => listingPriceCue(cruiseInfo.value));
    // Stale collections hide the dollar. The reason is "Price not current"
    // when the API still has a fare; the age note dates the last observation
    // and is not passed as the fresh Updated cue.
    const staleAgeNote = computed(() => staleAgeStamp(cruiseInfo.value));
    const hiddenPriceLabel = computed(() => hiddenPriceReason(cruiseInfo.value));

    // CP-68/70: HOT only for an honest price drop; BEST only beside a shown fare.
    // Unpublished shells are not shoppable, so neither badge (nor its $ copy) shows.
    const showHotDeal = computed(() => (
      showsShoppableFare(cruiseInfo.value, showsHotDealBadge(cruiseInfo.value))
    ));
    const showBestDeal = computed(() => (
      showsShoppableFare(cruiseInfo.value, showsBestDealBadge(cruiseInfo.value))
    ));

    // Chart only with ≥2 distinct observation times (not multi-cabin same day).
    // CP-69: a book box that hides the dollar does not also show Current/Lowest/Highest.
    // CP-90: an unpublished shell does not show priced cabin widgets.
    // CP-91: when this is false the section stays up with "Price history unavailable".
    // CP-94: one observation day is not a chart ("Only one price observation so far").
    const priceHistoryShoppable = computed(() => (
      showsShoppableFare(cruiseInfo.value, showsListingPrice(cruiseInfo.value))
    ));
    const hasPriceHistoryChart = computed(() => {
      if (!priceHistoryShoppable.value) return false;
      return observationDayCount(priceData.value) >= 2;
    });
    const isSinglePriceObservation = computed(() => (
      singlePriceObservation(priceHistoryShoppable.value, priceData.value)
    ));

    // Percent on the collapsed cabin card. Compared within that cabin only:
    // sorting every cabin together made Interior show Vista Suite's move
    // (↑962%) or Ocean View's (↑76%). A flat series is hidden so 0 does not
    // render as a down arrow. Defined below, after the visible cabin series.

    // Human-readable label for the trend window, kept in sync with dateRange.
    const trendLabel = computed(() => {
      switch (dateRange.value) {
        case '7': return 'in last 7 days';
        case '30': return 'in last 30 days';
        case '90': return 'in last 90 days';
        case 'all': return 'all time';
        default: return '';
      }
    });

    // Create a function to filter price data by date range
    const filteredPriceData = computed(() => {
      if (priceData.value.length === 0) return [];
      
      if (dateRange.value === 'all') {
        return priceData.value;
      }
      
      const now = new Date();
      const daysAgo = new Date(now);
      daysAgo.setDate(now.getDate() - parseInt(dateRange.value));
      
      return priceData.value.filter(p => new Date(p.timestamp) >= daysAgo);
    });

    // Group price data by cabin type
    const pricesByType = computed(() => {
      if (filteredPriceData.value.length === 0) return {};
      
      const result = {};
      
      // Get unique cabin types
      const cabinTypes = [...new Set(filteredPriceData.value.map(p => p.cabin_type || 'Unknown'))];
      
      cabinTypes.forEach(cabinType => {
        // Same series the chart plots. A higher cabin that only repeats
        // Interior is omitted, so Current / Lowest / Highest cannot show
        // that stamp or the swing it creates. Interior is unchanged.
        const series = chartSeriesPoints(filteredPriceData.value, cabinType);
        if (!series.length) return;

        const amounts = series
          .map((p) => Number(p.price))
          .filter((price) => Number.isFinite(price));
        if (!amounts.length) return;

        // CP-268: never the newest chart point. A cabin the latest
        // collection did not price has no Current (history only).
        result[cabinType] = {
          current: cabinCurrentFare(cruiseInfo.value, cabinType),
          lowest: Math.min(...amounts),
          highest: Math.max(...amounts),
          data: series,
        };
      });
      
      return result;
    });
    
    // CP-135: the toggle only shows with 2+ cabin types. With one, the
    // page stays collapsed and that cabin is the one shown.
    const showCabinTypeToggle = computed(() => showsCabinTypeToggle(pricesByType.value));
    const cabinTypesExpanded = computed(() => showAllCabinTypes.value && showCabinTypeToggle.value);

    // Filtered version of pricesByType that only shows Interior cabins when collapsed
    // (or the only cabin type, when there is just one).
    const visiblePricesByType = computed(() => {
      if (cabinTypesExpanded.value) {
        return pricesByType.value;
      }
      
      const result = {};
      collapsedCabinTypes(pricesByType.value).forEach((cabinType) => {
        result[cabinType] = pricesByType.value[cabinType];
      });
      return result;
    });

    // CP-98: all-cabins tiles cheap -> premium. Display order only.
    const orderedCabinTiles = computed(() => cabinTilesCheapToPremium(pricesByType.value));

    // Same points as the single cabin card. Best Time to Book reads this
    // series so it cannot describe a different cabin's move.
    const observedCabinSeries = computed(() => {
      const cards = Object.values(visiblePricesByType.value);
      if (cards.length !== 1) return [];
      return cards[0].data || [];
    });

    const priceTrend = computed(() => {
      const pct = cabinTrajectoryPercent(observedCabinSeries.value);
      if (pct == null || pct === 0) return null;
      return pct;
    });

    const dateRange = ref('all'); // Options: '7', '30', '90', 'all'
    
    // CP-290: the series the chart actually draws (also scopes the offer-only note).
    const plottedChartSeries = computed(() => {
      if (filteredPriceData.value.length === 0) return [];
      // Determine which cabin types to show based on the toggle
      let cabinTypesToShow;
      if (cabinTypesExpanded.value) {
        // Show all cabin types
        cabinTypesToShow = [...new Set(filteredPriceData.value.map(p => p.cabin_type || 'Unknown'))];
      } else {
        // Show only Interior cabin type (or the only cabin type, CP-135)
        cabinTypesToShow = collapsedCabinTypes(pricesByType.value);
      }
      
      // Plot the isolated series only. Interior-equal stamps on a higher
      // cabin, and swapped spikes, are omitted so they cannot set the scale.
      // A cabin with no fare of its own is left off the legend.
      return cabinTypesToShow
        .map((cabinType) => ({
          cabinType,
          points: chartSeriesPoints(filteredPriceData.value, cabinType),
        }))
        .filter((series) => series.points.length > 0);
    });

    const chartData = computed(() => {
      if (filteredPriceData.value.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }
      
      const plotted = plottedChartSeries.value;
      const allDates = [...new Set(plotted.flatMap((series) => (
        series.points.map((p) => new Date(p.timestamp).toLocaleDateString())
      )))].sort((a, b) => new Date(a) - new Date(b));
      
      // Create a dataset for each cabin type that still has a fare
      const datasets = plotted.map((series, index) => {
        // Define colors for each cabin type
        const colors = [
          { bg: 'rgba(66, 133, 244, 0.2)', border: 'rgba(66, 133, 244, 1)' },  // Blue
          { bg: 'rgba(52, 168, 83, 0.2)', border: 'rgba(52, 168, 83, 1)' },    // Green
          { bg: 'rgba(251, 188, 5, 0.2)', border: 'rgba(251, 188, 5, 1)' },    // Yellow
          { bg: 'rgba(234, 67, 53, 0.2)', border: 'rgba(234, 67, 53, 1)' },    // Red
          { bg: 'rgba(103, 58, 183, 0.2)', border: 'rgba(103, 58, 183, 1)' },  // Purple
        ];
        
        const colorIndex = index % colors.length;
        
        const cabinData = series.points;
        
        // Create a map of date to price for this cabin type
        const priceByDate = {};
        cabinData.forEach(p => {
          const dateStr = new Date(p.timestamp).toLocaleDateString();
          priceByDate[dateStr] = p.price;
        });
        
        // Create data array with prices for each date (or null if no price for that date)
        const data = allDates.map(date => priceByDate[date] || null);
        
        return {
          label: series.cabinType || 'Unknown',
          backgroundColor: colors[colorIndex].bg,
          borderColor: colors[colorIndex].border,
          pointBackgroundColor: colors[colorIndex].border,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: colors[colorIndex].border,
          data: data,
          // Monotone cubic: the spline cannot pass the recorded high or low.
          ...priceHistoryLineStyle,
        };
      });
      
      return {
        labels: allDates,
        datasets: datasets
      };
    });

    const baseChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 0,
      devicePixelRatio: window.devicePixelRatio || 1,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      // CP-248: these options now reach Chart.js (LineChart passes chartOptions). The rotated x labels are
      // laid out inside the canvas, so no extra bottom padding is needed (100 here + 180px of CSS padding was
      // the tall empty gap above Price per Night). Top padding, x ticks and legend display depend on the
      // width (detailResponsiveOptions below).
      layout: {
        padding: {
          bottom: 10,
          left: 10,
          right: 10
        }
      },
      scales: {
        // CP-248 follow-up: whole-dollar steps (>= $1, no cents) and one tick per label (10277's flat series).
        // CP-276: y span at least max($20, 5% of the max price), centred on the data.
        y: mergeChartOptions(priceYAxis((value) => formatPrice(value)), {
          beginAtZero: false, // Allow the chart to scale automatically
          grid: {
            color: 'rgba(200, 200, 200, 0.2)'
          },
          ticks: {
            font: {
              family: "'Roboto', sans-serif",
              size: 12
            }
          }
        }),
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              family: "'Roboto', sans-serif",
              size: 11
            },
            padding: 20,
            autoSkip: true
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: {
              family: "'Roboto', sans-serif",
              size: 12
            },
            usePointStyle: true
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: {
            family: "'Roboto', sans-serif",
            size: 14
          },
          bodyFont: {
            family: "'Roboto', sans-serif",
            size: 14
          },
          callbacks: {
            label: function(context) {
              return context.dataset.label + ': ' + formatPrice(context.raw);
            }
          }
        }
      }
    };

    // CP-248 (UI/UX, 390px): phone-width chart options follow the same ≤480px viewport query as the CSS.
    const narrowMql = (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
      ? window.matchMedia(NARROW_CHART_QUERY) : null;
    const narrowChart = ref(!!(narrowMql && narrowMql.matches));
    const onNarrowChange = (e) => { narrowChart.value = e.matches; };
    if (narrowMql) {
      if (narrowMql.addEventListener) narrowMql.addEventListener('change', onNarrowChange);
      else if (narrowMql.addListener) narrowMql.addListener(onNarrowChange);
    }
    onBeforeUnmount(() => {
      if (!narrowMql) return;
      if (narrowMql.removeEventListener) narrowMql.removeEventListener('change', onNarrowChange);
      else if (narrowMql.removeListener) narrowMql.removeListener(onNarrowChange);
    });
    const chartLegendShown = computed(() => detailLegendShown(narrowChart.value, cabinTypesExpanded.value));
    const chartOptions = computed(() => mergeChartOptions(baseChartOptions, detailResponsiveOptions({
      narrow: narrowChart.value,
      allCabinTypesShown: cabinTypesExpanded.value,
    })));

    // Drops a response that lost the race to a newer sail/id load.
    let loadGeneration = 0;

    // Function to load cruise data
    const loadCruiseData = async (id) => {
      const generation = ++loadGeneration;
      loading.value = true;
      error.value = null;
      notFound.value = false;
      // Stay on the spinner while we redirect to the sail-scoped prices call.
      // Committing the unscoped payload first paints a From price (sometimes
      // without that sail's Updated cue) before the Hot/Home query refetch.
      let holdForSail = false;

      try {
        // Get the sail_date from the query parameters if it exists.
        // Hot and Home links pass the displayed departure here; freshness
        // comes from that same prices payload as the From fare.
        const sailDate = route.query.sail_date;
        const todayStr = todayUtcDay();
        const requestedDay = sailDate ? sailDay(sailDate) : '';
        // A departed sail in the URL is not an available sailing.
        const requestedIsPast = Boolean(requestedDay) && requestedDay < todayStr;
        // Load full observation history (API default days=30 falsely empties stale series
        // and makes Detail price disagree with Browse latest current_price).
        const params = { days: 3650 };
        if (sailDate && !requestedIsPast) params.sail_date = sailDate;
        const response = await api.getCruisePrices(id, params);
        if (generation !== loadGeneration) return;

        const info = response.data.cruise_info || {};
        const prices = (response.data.prices || []).slice().sort((a, b) =>
          new Date(a.timestamp) - new Date(b.timestamp)
        );

        // When no upcoming sail is selected, default to this row's own
        // upcoming departure (CP-252), else the soonest upcoming one, and
        // refetch before anything is shown. The prices API scopes lead-in
        // and Updated to this query param. Never land on a past sail.
        const next = pickDefaultSailDate(info, isUpcomingSail, sailDay);
        if ((!sailDate || requestedIsPast) && next && sailDay(next) !== requestedDay) {
          holdForSail = true;
          router.replace({ path: route.path, query: { ...route.query, sail_date: next } });
          return;
        }

        cruiseInfo.value = info;
        // Drop departed sailings so an unscoped payload cannot chart them.
        priceData.value = prices.filter((p) => {
          const day = sailDay(p.sail_date);
          return !day || day >= todayStr;
        });

        // Reset per cruise so a previous ship profile cannot carry over.
        shipInfo.value = info.ship_info || null;

        if (sailDate && !requestedIsPast) {
          currentSailDate.value = sailDate;
        } else if (next) {
          currentSailDate.value = next;
        } else if (info.departure_date && isUpcomingSail(info.departure_date)) {
          currentSailDate.value = info.departure_date;
        } else {
          currentSailDate.value = null;
        }

        loadAdditionalData(id);

        console.log("Cruise info:", cruiseInfo.value);
        console.log("Available sail dates:", cruiseInfo.value.available_sail_dates);
        console.log("Current sail date:", currentSailDate.value);

      } catch (err) {
        if (generation !== loadGeneration) return;
        // CP-202: the API 404s for a missing or hidden sailing; that is not a retryable error.
        if (isNotFoundError(err)) {
          notFound.value = true;
        } else {
          error.value = 'Failed to load cruise data: ' + err.message;
        }
      } finally {
        if (generation === loadGeneration && !holdForSail) {
          loading.value = false;
        }
      }
    };

    onMounted(async () => {
      // Load initial data
      await loadCruiseData(cruiseId.value);
    });

    // Watch for changes to the cruise ID parameter
    watch(() => route.params.id, async (newId) => {
      // When the route parameter changes, reload the data
      if (newId) {
        await loadCruiseData(newId);
        
        // Scroll to top of page for better user experience
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
    
    // Add a watcher for query parameter changes
    watch(() => route.query.sail_date, async () => {
      if (cruiseId.value) {
        // Reload data when the sail_date query parameter changes
        await loadCruiseData(cruiseId.value);
        
        // Scroll to top of page for better user experience
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });

    // Get the first port of call from the itinerary. Empty when the schedule has none.
    const getFirstPort = computed(() => {
      const days = itinerarySchedule.value || [];
      const firstPortDay = days.find(day => day.port || day.location);
      return firstPortDay ? (firstPortDay.port || firstPortDay.location) : '';
    });

    // Get the last port of call from the itinerary.
    const getLastPort = computed(() => {
      const days = itinerarySchedule.value || [];
      for (let i = days.length - 1; i >= 0; i--) {
        const day = days[i];
        if (day.port || day.location) {
          return day.port || day.location;
        }
      }
      return '';
    });
    
    // One count rule for the timeline header and the sidebar (CP-214): distinct
    // ports, embark/debark at one port once, sea days excluded.
    const itineraryCounts = computed(() => summarizeItinerary(itinerarySchedule.value, portsOfCall.value));

    // Helper: get YYYY-MM-DD for comparison
    const toDateString = (dateString) => toDateStringUtil(dateString);

    // Helper: first sail date >= today (next to sail)
    const getNextToSailDate = (dateStrings) => {
      if (!dateStrings || dateStrings.length === 0) return null;
      const future = dateStrings.find(d => isUpcomingSail(d));
      return future || null;
    };

    // Upcoming sail dates only. A departed sailing is not available.
    const availableSailDates = computed(() => {
      const dates = (cruiseInfo.value && cruiseInfo.value.available_sail_dates) || [];
      return dates.filter(d => isUpcomingSail(d));
    });

    const visibleSailDates = computed(() => availableSailDates.value);
    
    // Prefer the selected upcoming sail, then the next one. Never a past date.
    const effectiveCurrentSailDate = computed(() => {
      if (currentSailDate.value && isUpcomingSail(currentSailDate.value)) {
        return currentSailDate.value;
      }
      const next = getNextToSailDate(availableSailDates.value);
      if (next) return next;
      if (cruiseInfo.value && cruiseInfo.value.departure_date
          && isUpcomingSail(cruiseInfo.value.departure_date)) {
        return cruiseInfo.value.departure_date;
      }
      return null;
    });
    
    // Check if a date is the current sail date
    const isSameSailDate = (date) => {
      if (!effectiveCurrentSailDate.value || !date) return false;
      return toDateString(date) === toDateString(effectiveCurrentSailDate.value);
    };
    
    // Format sail date for display
    const formatSailDate = (dateString) => {
      if (!dateString) return 'Date not available';
      return formatSailDateUtil(dateString, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatDepartureDisplay = (dateString) => {
      if (!dateString) return 'TBD';
      return formatSailDateUtil(dateString, { year: 'numeric', month: 'long', day: 'numeric' });
    };
    
    // Navigate to a different sail date
    const navigateToSailDate = (date) => {
      if (!isUpcomingSail(date)) return;
      if (isSameSailDate(date)) return; // Don't navigate if it's the current date
      
      // Navigate to the same cruise but with a different sail date as a query parameter
      router.push({
        path: `/cruise/${cruiseId.value}`,
        query: { sail_date: date }
      });
      
      // Or if each sail date has its own cruise ID:
      // You would need to fetch the cruise ID for this sail date first
    };

    // CP-238: plain click / Enter on a Sail Dates link stays in the SPA; modified clicks open the href.
    const onSailDateClick = (event, date) => {
      if (!isPlainActivation(event)) return;
      event.preventDefault();
      navigateToSailDate(date);
    };
    
    // Real Viator experiences, fetched once per sailing. Stops render from the
    // schedule without waiting on this; an empty or failed payload shows no tours.
    let excursionsRequest = 0;
    const loadExcursions = async (id) => {
      const request = ++excursionsRequest;
      excursionsPayload.value = null;
      excursionsLoading.value = true;
      try {
        const excursionsResponse = await api.getCruiseExcursions(id);
        if (request === excursionsRequest) excursionsPayload.value = excursionsResponse.data;
      } catch (err) {
        if (request === excursionsRequest) excursionsPayload.value = null;
        void err;
      } finally {
        if (request === excursionsRequest) excursionsLoading.value = false;
      }
    };

    // Load additional data (images, etc.)
    const loadAdditionalData = async (id) => {
      const excursions = loadExcursions(id);
      try {
        // Load cruise images
        const imagesResponse = await api.getCruiseImages(id);
        cruiseImages.value = imagesResponse.data;
      } catch (err) {
        console.error('Error loading additional data:', err);
        // Non-critical, continue without images
      }
      await excursions;
    };

    const timelineModel = computed(() => buildTimelineModel({
      days: itinerarySchedule.value,
      payload: excursionsPayload.value,
      duration: cruiseInfo.value.duration,
      unpublished: scheduleUnpublished.value,
    }));

    // The Viator disclosure shows once, and only while a tour link is rendered.
    const itineraryHasExcursionSuggestions = computed(() => timelineModel.value.hasExcursionSuggestions);
    
    // Get unique cabin types for price alert
    const uniqueCabinTypes = computed(() => {
      const types = new Set(priceData.value.map(p => p.cabin_type).filter(t => t));
      return Array.from(types);
    });
    
    // CP-290 (AC7): note only for plotted cabins the API reports as having
    // offer-code-like rows kept out of `prices` (cruise_info.offer_only_cabins).
    const offerOnlyHistoryNote = computed(() => offerOnlyNote(
      plottedChartSeries.value.map((series) => series.cabinType),
      cruiseInfo.value?.offer_only_cabins,
      orderedCabinTiles.value.map(([cabinType]) => cabinType),
    ));

    // Get lowest and highest prices for pricing enhancements
    const lowestPrice = computed(() => {
      if (priceData.value.length === 0) return null;
      return Math.min(...priceData.value.map(p => p.price));
    });
    
    const highestPrice = computed(() => {
      if (priceData.value.length === 0) return null;
      return Math.max(...priceData.value.map(p => p.price));
    });

    // Deal badge popover definitions, sourced from the server deal_status
    const hotDealDefinitions = computed(() => {
      const details = cruiseInfo.value.deal_status?.hot_deal_details || {};
      const amount = details.price_drop_amount;
      const pct = details.price_drop_percentage;
      const peak = details.peak_price;
      const cabin = details.current_cabin_type;

      const amountStr = typeof amount === 'number' && amount > 0
        ? formatPrice(amount)
        : null;
      const pctStr = typeof pct === 'number' ? `${Math.round(pct)}% drop` : null;

      let priceDropBody;
      if (amountStr && pctStr) priceDropBody = `Save ${amountStr} (${pctStr})`;
      else if (amountStr) priceDropBody = `Save ${amountStr}`;
      else if (pctStr) priceDropBody = pctStr;
      else priceDropBody = 'Dropped below its 30-day recorded peak.';
      if (cabin && cabin !== 'Unknown') priceDropBody += ` \u2014 ${cabin}`;

      const defs = [{ label: 'Price Drop', body: priceDropBody }];
      if (typeof peak === 'number' && peak > 0) {
        defs.push({
          label: 'Baseline',
          body: `Compared against our 30-day recorded peak of ${formatPrice(peak)} for this cabin category.`
        });
      }
      defs.push({
        label: 'Data basis',
        body: 'Tracked daily from cruise line booking sources.'
      });
      return defs;
    });

    const bestDealDefinitions = computed(() => {
      const duration = Number(cruiseInfo.value.duration);
      const price = Number(currentPrice.value);
      const defs = [];
      if (duration > 0 && Number.isFinite(price)) {
        defs.push({
          label: 'Best Value',
          body: `${formatPrice(price / duration)} / night`
        });
      } else {
        defs.push({ label: 'Best Value', body: 'Lowest fare across voyage length.' });
      }
      defs.push({ label: 'Baseline', body: 'Lowest fare across voyage length.' });
      defs.push({
        label: 'How calculated',
        body: 'Calculated by dividing lowest cabin fare by voyage length.'
      });
      return defs;
    });

    const retryLoad = () => loadCruiseData(cruiseId.value);

    const titleCase = (str) => formatPlaceTitle(str);

    const nightsLabel = (n) => plainNightsLabel(n);

    const shareDescription = computed(() => {
      if (scheduleUnpublished.value) return 'Schedule not yet published';
      const nights = nightsLabel(cruiseInfo.value.duration);
      const ship = shipUnknown.value ? '' : (cruiseInfo.value.ship_name || '');
      if (nights && ship) return `${nights} cruise on ${ship}`;
      if (nights) return `${nights} cruise`;
      if (ship) return `Cruise on ${ship}`;
      return cruiseInfo.value.cruise_line || '';
    });

    return {
      stacked,
      offerOnlyHistoryNote,
      formatPriceAmount,
      formatPrice,
      cruiseInfo,
      priceData,
      loading,
      error,
      notFound,
      retryLoad,
      chartData,
      chartOptions,
      chartLegendShown,
      portsOfCall,
      itinerarySection,
      itinerarySchedule,
      itineraryTitlePorts,
      itineraryEmptyMessage,
      timelineModel,
      excursionsLoading,
      itineraryHasExcursionSuggestions,
      itineraryCounts,
      portsLabel,
      scheduleUnpublished,
      shipUnknown,
      showShipModule,
      reviewCount,
      realDeparturePort,
      realDestination,
      shareDescription,
      priceTrend,
      trendLabel,
      cruiseId,
      affiliateEnabled,
      bookPartnerUrl,
      currentPrice,
      priceUpdatedLabel,
      staleAgeNote,
      hiddenPriceLabel,
      showHotDeal,
      showBestDeal,
      hasPriceHistoryChart,
      isSinglePriceObservation,
      dateRange,
      filteredPriceData,
      pricesByType,
      visiblePricesByType,
      orderedCabinTiles,
      observedCabinSeries,
      showAllCabinTypes,
      showCabinTypeToggle,
      cabinTypesExpanded,
      effectiveCurrentSailDate,
      getFirstPort,
      getLastPort,
      availableSailDates,
      visibleSailDates,
      isSameSailDate,
      formatSailDate,
      formatDepartureDisplay,
      navigateToSailDate,
      onSailDateClick,
      sailDateHref,
      sailDateRowLabel,
      cruiseImages,
      shipInfo,
      uniqueCabinTypes,
      lowestPrice,
      highestPrice,
      hotDealDefinitions,
      bestDealDefinitions,
      titleCase,
      nightsLabel,
      sailingNightsLabel
    };
  }
}
</script>

<style scoped>
.cruise-detail {
  margin: 0;
  padding: 0;
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)); /* CP-213: room for the 56px bar */
  font-family: var(--font-body);
  color: var(--color-text);
}

.cruise-detail--unpublished {
  padding-bottom: var(--space-6);
}

@media (min-width: 1024px) {
  .cruise-detail,
  .cruise-detail--unpublished {
    padding-bottom: 0;
  }
}

.unpublished-booking .schedule-unpublished {
  margin-top: 0;
}

.unpublished-booking-note {
  margin: var(--space-2) 0 0;
  line-height: var(--line-height-body);
  color: var(--color-text-muted);
}

/* Loading and Error States */
.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
}

/* CP-211: while loading, the box is taller than the viewport, so the footer stays below the fold
   and the page content that replaces it cannot push anything visible. */
.loading-container {
  min-height: 100vh;
  min-height: 100svh;
}

.loading-container p, .error-container p {
  color: var(--color-text-muted);
}

.spinner {
  border: 4px solid var(--color-border-light);
  border-radius: 50%;
  border-top-color: var(--color-primary);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-5);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container i {
  font-size: 48px;
  color: var(--color-error);
  margin-bottom: var(--space-5);
}

/* Two Column Layout: moved to components/DetailLayout.vue (CP-226) */

/* Card Styling */
.card {
  background-color: var(--color-surface);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  padding: var(--space-5);
  margin-bottom: var(--space-5);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-card-hover);
}

.card h2 {
  margin-top: 0;
  margin-bottom: var(--space-5);
  font-size: var(--text-h2);
  color: var(--color-primary);
  border-bottom: 2px solid var(--color-border-light);
  padding-bottom: var(--space-3);
}

/* Cruise Header Card */
.cruise-header-card {
  background-image: linear-gradient(135deg, var(--cs-hero-from) 0%, var(--cs-hero-to) 100%);
  color: white;
}

.cruise-header-card h1, .cruise-header-card h2, .cruise-header-card h3 {
  color: white;
  margin: 0;
}

.cruise-title {
  margin-bottom: var(--space-4);
}

.title-with-badges {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.title-with-badges h1 {
  margin: 0;
  flex: 1;
  min-width: 200px;
  font-family: var(--font-heading);
}

.deal-badges {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.deal-badge {
  display: inline-block;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-pill);
  font-size: var(--text-small);
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  box-shadow: var(--shadow-nav);
  cursor: pointer;
}

.deal-badge-info {
  font-size: 0.85em;
  opacity: 0.85;
  margin-left: 2px;
}

/* CP-277 (UI/UX call 2026-10-21): no pulse. The HOT DEAL badge is static at full opacity in both themes, with or
   without prefers-reduced-motion: the old 0.9-opacity frame put white on the dark fill below 4.5:1. */
.hot-deal-badge {
  background-color: var(--cs-deal-accent);
  color: white;
  opacity: 1;
  animation: none;
  transition: none;
}

/* CP-283 (UI/UX follow-up to CP-277): no pulse. The BEST DEAL badge is static at full opacity in both themes, with
   or without prefers-reduced-motion (the 0.9-opacity, 1.05-scale frame is gone). */
.best-deal-badge {
  background-color: var(--cs-amber);
  color: var(--cs-on-amber);
  opacity: 1;
  animation: none;
  transition: none;
}

/* Still used by .hot-deal-badge until CP-277 (#218) lands; delete once nothing references it (CP-283 AC 5). */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.05);
  }
}

.cruise-subtitle {
  display: flex;
  gap: 10px;
  font-size: 18px;
  margin-top: 8px;
}

.ship-name {
  font-weight: bold;
}

.cruise-line {
  opacity: 0.9;
}

.cruise-summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5);
  margin-top: var(--space-4);
}

.summary-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background-color: rgba(255, 255, 255, 0.2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-pill);
}

.summary-item i {
  font-size: 16px;
}

.hero-title {
  color: white !important;
  font-size: inherit;
  line-height: inherit;
}

.subtitle-sep {
  opacity: 0.7;
  margin: 0 2px;
}

.booking-share-rail {
  margin-top: var(--space-4);
  padding-top: var(--space-3);
  border-top: 1px solid var(--cs-card-border);
}

.booking-card {
  /* Keep Book Now visually dominant */
}

/* CP-213: the phone Book bar (position: fixed) lives inside this card. The
   generic .card:hover lift is a transform, and a transformed ancestor becomes
   the fixed bar's containing block: on touch the sticky :hover then threw the
   bar off screen and swallowed taps. This card never lifts. */
.booking-card:hover {
  transform: none;
}

/* Price Chart Card */
.price-chart-card {
  position: relative;
  overflow: visible;
  padding-bottom: 60px;
}

.price-summary {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 20px;
  gap: 15px;
}

.price-stat {
  display: flex;
  flex-direction: column;
  background-color: var(--color-background);
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-md);
  min-width: 100px;
  text-align: center;
  overflow: hidden;
  word-wrap: break-word;
}

.stat-label {
  font-size: var(--text-small);
  color: var(--color-text-muted);
  margin-bottom: var(--space-1);
  overflow-wrap: break-word;
}

.stat-value {
  font-size: var(--text-h2);
  font-weight: bold;
  color: var(--color-primary);
  overflow-wrap: break-word;
  word-wrap: break-word;
  line-height: 1.2;
  max-width: 100%;
}

.price-trend {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  border-radius: 6px;
  min-width: 100px;
}

.trend-indicator {
  font-size: 20px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* CP-87: ↑ is a rise (bad — red). ↓ is a drop (good — green). */
.trend-up {
  color: var(--color-error);
}

.trend-down {
  color: var(--color-accent-success);
}

.trend-label {
  font-size: var(--text-caption);
  color: var(--color-text-muted);
  margin-top: 4px;
}

.chart-container {
  --chart-gap: 24px;
  --chart-ink-inset: 5px;
  width: 100%;
  max-width: 100%;
  margin: 0 0 20px;
  position: relative;
  overflow: visible !important;
  /* CP-248: Chart.js lays the rotated x labels out inside the canvas; the old 180px bottom padding was empty.
     Top: the button-to-chart gap (see .chart-controls). */
  padding: calc(var(--chart-gap) - var(--chart-ink-inset)) 20px 20px;
  box-sizing: border-box;
}

.chart-container--legend {
  --chart-ink-inset: 6px; /* LEGEND_LABEL_PADDING (10) less the dot's ~4px overhang */
}

.chart-container :deep(canvas) {
  max-width: 100% !important;
  width: 100% !important;
  display: block;
}

.chart-container :deep(.line-chart-wrapper) {
  width: 100%;
  position: relative;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--color-text-muted);
  background-color: var(--color-background);
  border-radius: var(--radius-md);
}

.no-data-message i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.view-more-link {
  text-align: center;
  margin-top: 15px;
}

.view-more-link a {
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.view-more-link a:hover {
  text-decoration: underline;
}

/* Itinerary: the timeline itself is styled in DetailTimeline.vue. */
.itinerary-card {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.cruise-package {
  margin: var(--space-2) 0 0;
  line-height: var(--line-height-body);
  font-weight: 600;
}

.schedule-unpublished {
  margin: 0;
  line-height: var(--line-height-body);
  color: var(--color-text-muted);
  margin-top: var(--space-2);
  font-weight: 600;
  font-style: italic;
}

.cruise-detail {
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)); /* CP-213: room for the 56px bar */
}

/* CP-213: compact phone hero. Chips wrap side by side instead of stacking
   full-width, so the price card and CTA stay on the first screen. */
.cruise-summary {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: var(--space-3);
}

.cruise-summary .summary-item {
  padding: var(--space-1) 10px;
  font-size: 13px;
  line-height: 1.3;
}

.cruise-summary .summary-item i {
  font-size: 13px;
}

.cruise-header-card .cruise-title {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .cruise-summary {
    gap: var(--space-4);
    margin-top: var(--space-4);
  }

  .cruise-summary .summary-item {
    padding: var(--space-2) var(--space-4);
    font-size: inherit;
    line-height: inherit;
  }

  .cruise-summary .summary-item i {
    font-size: 16px;
  }

  .cruise-header-card .cruise-title {
    margin-bottom: var(--space-4);
  }
}

/* CP-213 / CP-226: on phone/tablet the Itinerary follows the price card, then the rest of the rail
   (Price Alert, Itinerary Summary, Sail Dates), then the remaining main cards. That order is now the
   DOM order (components/DetailLayout.vue, stacked < 1024px), not CSS `order`. */

/* CP-213: tighter phone spacing so the first screen holds the hero, the
   price card with its CTA, and the start of the Itinerary. */
@media (max-width: 767px) {
  .cruise-detail .card {
    margin-bottom: var(--space-4);
  }

  .cruise-header-card {
    padding: var(--space-3) var(--space-4);
  }

  .cruise-header-card .title-with-badges h1 {
    min-width: 0;
    font-size: 1.0625rem;
    line-height: 1.25;
  }

  .cruise-header-card .cruise-subtitle {
    display: block;
    margin-top: 2px;
    font-size: 1rem;
    line-height: 1.3;
  }

  .booking-card {
    padding: var(--space-3);
  }

  .booking-share-rail {
    margin-top: var(--space-2);
    padding-top: var(--space-2);
  }

  .itinerary-card h2 {
    margin-bottom: var(--space-2);
    padding-bottom: var(--space-2);
    font-size: 1.5rem;
    line-height: 1.2;
  }
}

@media (min-width: 1024px) {
  .cruise-detail {
    padding-bottom: var(--space-6);
  }
}

@media (max-width: 767px) {
  .price-summary {
    justify-content: center;
  }
  
  .price-stat, .price-trend {
    min-width: 45%;
  }
  
  .stat-value {
    font-size: 18px;
  }
  
  .cabin-prices .stat-value {
    font-size: 16px;
  }
  
}

/* CP-248: the gap from the 7/30/90/All Time buttons to the first chart pixel is --chart-gap (24px; 16px at
   ≤480px). It is the .chart-container top padding plus how far below the canvas top Chart.js paints its first pixel
   (--chart-ink-inset, measured in Chrome from the canvas pixels):
     - legend hidden (phone, one cabin type): the top y-tick label, 5px down;
     - legend shown: the point-style dot, 6px down (legend labels.padding 10, LEGEND_LABEL_PADDING in
       utils/chartOptions.js, less the ~4px the dot overhangs its 12px row).
   No margins between the buttons and the chart. */
.chart-controls {
  display: flex;
  justify-content: center;
  margin-bottom: 0;
}

/* Date Range Selector */
.date-range-selector {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 0;
}

.range-button {
  background-color: var(--color-border-light);
  border: none;
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-4);
  font-size: var(--text-small);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background var(--transition-normal);
}

.range-button:hover {
  background-color: var(--color-primary-light);
}

.range-button.active {
  background-color: var(--cs-fill);
  color: white;
}

/* Cabin Price Cards */
.all-cabin-prices {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(250px, 100%), 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.cabin-price-card {
  background-color: var(--color-background);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  width: 100%;
  overflow: hidden;
  min-width: 0;
}

.cabin-type {
  margin-top: 0;
  margin-bottom: var(--space-3);
  font-size: var(--text-h3);
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--space-2);
  overflow-wrap: break-word;
  word-wrap: break-word;
}

/* CP-98: stats wrap between tiles, never inside an amount. Each stat is
   at least as wide as its amount; the row wraps when they do not fit. */
.cabin-prices {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

.cabin-prices .price-stat {
  flex: 1 1 0%;
  min-width: max-content;
  background-color: var(--color-surface);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  text-align: center;
}

.cabin-prices .stat-value {
  font-size: 20px;
  white-space: nowrap;
  overflow-wrap: normal;
  word-wrap: normal;
  word-break: keep-all;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

/* CP-290 (AC7): muted history-only note under the price chart. */
.offer-only-note {
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  text-wrap: pretty;
}

/* CP-268: "Not in latest update" sits in the Current value slot at label size in the muted
   token (--color-text-muted on the --color-surface tile: 5.93:1 light, 6.72:1 dark; pinned in
   cabinCurrentFare.test.cjs). It wraps inside the slot instead of widening it (min-width 0), so
   the tile keeps its width and the three stats stay in one row (15255 Veranda). */
.cabin-prices .stat-note {
  font-size: 12px;
  color: var(--color-text-muted);
  line-height: 1.2;
  white-space: normal;
  overflow-wrap: break-word;
}

.cabin-prices .price-stat--no-current {
  min-width: 0;
}

.cabin-prices .stat-label {
  font-size: 12px;
  overflow-wrap: break-word;
}

@media (max-width: 768px) {
  .cabin-prices {
    flex-direction: column;
    gap: 8px;
  }

  .cabin-prices .price-stat {
    flex: 0 0 auto;
  }
}

.chart-wrapper {
  width: 100%;
  clear: both;
  margin-bottom: 20px;
  overflow: visible !important;
  position: relative;
  padding-bottom: 0;
}

.chart-container-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: visible !important;
  position: relative;
}

@media (max-width: 480px) {
  .card {
    padding: 16px;
  }
  
  .price-stat, .price-trend {
    min-width: 100%;
  }
  
  .stat-value {
    font-size: 16px;
  }
  
  .cabin-prices .stat-value {
    font-size: 14px;
  }
  
  .chart-container {
    --chart-gap: 16px;
    height: auto;
  }
}

/* Cabin toggle button styles */
.cabin-toggle-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 8px 0 16px 0;
}

.cabin-toggle-button {
  background-color: var(--color-border-light);
  border: none;
  border-radius: var(--radius-pill);
  padding: var(--space-2) var(--space-4);
  font-size: var(--text-small);
  color: var(--color-primary);
  cursor: pointer;
  transition: background var(--transition-normal);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.cabin-toggle-button:hover {
  background-color: var(--color-primary-light);
}

.cabin-toggle-button i {
  font-size: 12px;
}

/* Itinerary Summary Card */
.itinerary-summary-card h3 {
  margin-top: 0;
  margin-bottom: var(--space-4);
  font-size: var(--text-h3);
  color: var(--color-primary);
  border-bottom: 1px solid var(--color-border-light);
  padding-bottom: var(--space-3);
}

.itinerary-summary-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.itinerary-summary-content .summary-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  /* Dark follow-up: the hero pill rule above (white at 20%) also matched these rows; over the dark card it
     painted #4b5051 under the muted labels (3.5:1). Over the white light card it was invisible, so
     transparent is the same pixels in light. */
  background-color: transparent;
}

.itinerary-summary-content .summary-item i {
  font-size: 18px;
  color: var(--color-primary);
  width: 24px;
  text-align: center;
}

.itinerary-summary-content .summary-item div {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: var(--text-caption);
  color: var(--color-text-muted);
}

.summary-value {
  font-size: var(--text-body);
  font-weight: 500;
  color: var(--color-text);
}

/* Sail Dates Card */
.sail-dates-card h3 {
  margin-top: 0;
  margin-bottom: var(--space-4);
  font-size: var(--text-h3);
  color: var(--color-primary);
  border-bottom: 1px solid var(--color-border-light);
  padding-bottom: var(--space-3);
}

.sail-dates-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-height: 300px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0;
}

.sail-date-item {
  color: inherit;
  text-decoration: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background-color: var(--color-background);
  transition: background var(--transition-normal);
  cursor: pointer;
}

.sail-date-item:hover {
  background-color: var(--color-primary-light);
}

/* CP-238: visible keyboard focus with the shared ring token. Inset so the scrolling list
   (overflow-y: auto) cannot clip it. */
.sail-date-item:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: -2px;
  background-color: var(--color-primary-light);
}

.sail-date-item.current-date {
  background-color: var(--color-primary-light);
  border-left: 3px solid var(--color-primary);
}

.date-info {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.date-info i {
  color: var(--color-primary);
}

.current-indicator {
  font-size: var(--text-caption);
  font-weight: 500;
  color: var(--color-primary);
  background-color: var(--color-primary-light);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
}

.view-indicator {
  color: var(--color-text-muted);
}

.no-dates-message {
  padding: var(--space-4);
  text-align: center;
  color: var(--color-text-muted);
  background-color: var(--color-background);
  border-radius: var(--radius-md);
}
</style>
